import React, { useState, Fragment } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, ArrowRight, Shield, AlertCircle, FileText } from 'lucide-react';
import { PolicyPage } from './PolicyPage';
import { AuditTrailPage } from './AuditTrailPage';
type GovernanceTab = 'policy-ai-act' | 'policy-gdpr' | 'audit' | 'risk' | 'security' | 'pii';
export function GovernancePage() {
  const [activeTab, setActiveTab] = useState<GovernanceTab>('policy-ai-act');
  const [searchParams] = useSearchParams();
  const serviceScope = searchParams.get('service');
  const isServiceScoped = !!serviceScope;
  const getServiceName = (serviceId: string) => {
    const serviceMap: Record<string, string> = {
      'service-1': 'Service 1',
      'service-2': 'Service 2',
      'service-3': 'Service 3'
    };
    return serviceMap[serviceId] || serviceId;
  };
  const isPolicyTab = activeTab.startsWith('policy-');
  const complianceTab = isPolicyTab ? activeTab.replace('policy-', '') as 'ai-act' | 'gdpr' : undefined;
  const tabs = [{
    id: 'policy-ai-act',
    label: 'AI Act',
    icon: AlertCircle,
    group: 'policy'
  }, {
    id: 'policy-gdpr',
    label: 'GDPR',
    icon: Shield,
    group: 'policy'
  }, {
    id: 'audit',
    label: 'Audit Trail',
    icon: FileText
  }, {
    id: 'risk',
    label: 'Risk'
  }, {
    id: 'security',
    label: 'Security'
  }, {
    id: 'pii',
    label: 'PII Detection'
  }];
  return <div className="min-h-screen bg-[#111114] pt-4 px-4 md:pt-8 md:px-8 pb-12 space-y-6 md:space-y-8" data-id="element-2447">
      {/* Header Section */}
      <div className="flex flex-col gap-1" data-id="element-2448">
        <h1 className="text-[24px] font-semibold text-white tracking-tight" data-id="element-2449">
          Reports & Governance
        </h1>
        <p className="text-[14px] text-white/40 max-w-2xl" data-id="element-2450">
          Centralized control for AI compliance, security policies, and audit
          trails. Monitor and manage your AI governance posture.
        </p>
      </div>

      {/* Service Scope Banner */}
      {isServiceScoped && serviceScope && <div className="bg-[#8eff01]/5 border border-[#8eff01]/20 rounded-xl p-4 flex items-center justify-between" data-id="element-2451">
          <div className="flex items-center gap-3" data-id="element-2452">
            <div className="p-2 bg-[#8eff01]/10 rounded-lg" data-id="element-2453">
              <Filter className="w-4 h-4 text-[#8eff01]" data-id="element-2454" />
            </div>
            <div data-id="element-2455">
              <p className="text-[13px] font-medium text-white" data-id="element-2456">
                Viewing: {getServiceName(serviceScope)}
              </p>
              <p className="text-[11px] text-white/40 mt-0.5" data-id="element-2457">
                Filtered data for this service
              </p>
            </div>
          </div>
          <Link to="/report" className="flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium text-[#8eff01] hover:bg-[#8eff01]/10 rounded-lg transition-all" data-id="element-2458">
            View all
            <ArrowRight className="w-3.5 h-3.5" data-id="element-2459" />
          </Link>
        </div>}

      {/* Tabs Navigation */}
      <div className="border-b border-white/[0.06]" data-id="element-2460">
        <div className="flex gap-1 overflow-x-auto pb-1" data-id="element-2461">
          {tabs.map((tab, index) => {
          const Icon = tab.icon;
          const showSeparator = index > 0 && tab.group !== 'policy' && tabs[index - 1].group === 'policy';
          return <Fragment key={tab.id} data-id="element-2462">
                {showSeparator && <div className="flex items-center px-2" data-id="element-2463">
                    <div className="w-px h-4 bg-white/10" data-id="element-2464" />
                  </div>}
                <button onClick={() => setActiveTab(tab.id as GovernanceTab)} className={`
                    flex items-center px-4 py-2.5 text-[13px] font-medium transition-all rounded-lg whitespace-nowrap relative
                    ${activeTab === tab.id ? 'text-white bg-white/[0.06]' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.02]'}
                  `} data-id="element-2465">
                  {Icon && <Icon className={`w-4 h-4 mr-2.5 ${activeTab === tab.id ? 'text-[#8eff01]' : 'opacity-60'}`} data-id="element-2466" />}
                  {tab.label}
                </button>
              </Fragment>;
        })}
        </div>
      </div>

      {/* Content Area */}
      <div className="min-h-[400px]" data-id="element-2467">
        {isPolicyTab && <PolicyPage hideHeader={true} hideTabs={true} complianceTab={complianceTab} data-id="element-2468" />}
        {activeTab === 'audit' && <AuditTrailPage hideHeader={true} data-id="element-2469" />}
        {activeTab === 'risk' && <PlaceholderTab title="Risk Assessment" description="AI risk evaluation and management" data-id="element-2470" />}
        {activeTab === 'security' && <PlaceholderTab title="Security" description="Security and vulnerability management" data-id="element-2471" />}
        {activeTab === 'pii' && <PlaceholderTab title="PII Detection" description="Personal data detection and handling" data-id="element-2472" />}
      </div>
    </div>;
}
function PlaceholderTab({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <div className="flex items-center justify-center py-24" data-id="element-2473">
      <div className="text-center" data-id="element-2474">
        <div className="w-14 h-14 bg-white/[0.03] border border-white/[0.06] rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-2475">
          <svg className="w-6 h-6 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2476">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" data-id="element-2477" />
          </svg>
        </div>
        <h3 className="text-[15px] font-medium text-white/70 mb-1" data-id="element-2478">{title}</h3>
        <p className="text-[12px] text-white/30" data-id="element-2479">{description}</p>
        <span className="inline-block mt-3 px-2.5 py-1 bg-white/[0.03] border border-white/[0.06] rounded-md text-[10px] text-white/30 font-medium" data-id="element-2480">
          Coming soon
        </span>
      </div>
    </div>;
}